import { type ReactNode, useState } from "react";

import {
  BoldIcon,
  Box,
  CodeBlockIcon,
  CodeIcon,
  IconButton,
  ItalicIcon,
  LinkIcon,
  ListBulletedIcon,
  ListNumberedIcon,
  Menu,
  MenuButton,
  MenuIconButton,
  MenuItem,
  MenuList,
  RedoIcon,
  StrikethroughSIcon,
  TableIcon,
  Text,
  UndoIcon,
} from "@hightouch/htui";
import { type Editor, useEditorState } from "@tiptap/react";

// All heading levels are legal in the schema, but the dropdown only offers the
// levels the Context Hub designs use; deeper headings still render and
// round-trip when a doc arrives with them.
const BLOCK_STYLE_LABELS: Record<number, string> = {
  0: "Paragraph",
  1: "Heading 1",
  2: "Heading 2",
  3: "Heading 3",
  4: "Heading 4",
  5: "Heading 5",
  6: "Heading 6",
};

const MENU_HEADING_LEVELS = [1, 2, 3] as const;

// Insert-table grid (Google-Docs style): starts at 10×5 and grows by one in
// whichever direction the pointer reaches the edge, capped at 20×20. The grid
// only shapes the picker, not the schema — bigger tables are still reachable
// via the in-table add-row/column items.
const TABLE_PICKER_BASE_COLS = 10;
const TABLE_PICKER_BASE_ROWS = 5;
const TABLE_PICKER_MAX = 20;
// Cell/gap geometry in px (not rem) so the picker renders identically
// regardless of the user's root font-size. The grid container gets an explicit
// px width derived from these, so the `repeat(n, 1fr)` columns each resolve to
// exactly TABLE_PICKER_CELL_PX.
const TABLE_PICKER_CELL_PX = 16;
const TABLE_PICKER_GAP_PX = 4;

const Divider = () => (
  <Box
    aria-hidden
    width="1px"
    height={5}
    backgroundColor="base.divider"
    mx={1}
    flexShrink={0}
  />
);

type MarkdownToolbarProps = {
  editor: Editor;
  /** Feature-specific controls appended after the built-in groups. */
  toolbarActions?: (editor: Editor) => ReactNode;
  /** Leaves out undo and redo, for a host that draws them elsewhere. */
  hideHistory?: boolean;
};

/**
 * Document-style formatting toolbar: undo/redo, a block-style dropdown
 * (Paragraph / Heading 1–3), inline marks, lists, and block styles. All icons
 * come from `@hightouch/htui`; no new icons are introduced.
 */
export const MarkdownToolbar = ({
  editor,
  toolbarActions,
  hideHistory = false,
}: MarkdownToolbarProps) => {
  const state = useEditorState({
    editor,
    selector: ({ editor: instance }) => ({
      isBold: instance.isActive("bold"),
      isItalic: instance.isActive("italic"),
      isStrike: instance.isActive("strike"),
      isCode: instance.isActive("code"),
      isLink: instance.isActive("link"),
      isBulletList: instance.isActive("bulletList"),
      isOrderedList: instance.isActive("orderedList"),
      isCodeBlock: instance.isActive("codeBlock"),
      isInTable: instance.isActive("table"),
      headingLevel:
        [1, 2, 3, 4, 5, 6].find((level) =>
          instance.isActive("heading", { level }),
        ) ?? 0,
      // can() throws on a destroyed editor, so check for that first.
      canUndo: !instance.isDestroyed && instance.can().undo(),
      canRedo: !instance.isDestroyed && instance.can().redo(),
    }),
  });

  const isInHeading = state.headingLevel > 0;

  // The table menu is controlled so the grid size picker (a non-MenuItem
  // child, which the menu's own closeOnSelect never sees) can close it on
  // insert.
  const [isTableMenuOpen, setIsTableMenuOpen] = useState(false);

  const setLink = () => {
    const previousUrl = editor.getAttributes("link").href as string | undefined;
    const url = window.prompt("URL", previousUrl ?? "");
    if (url === null) return;
    if (url === "") {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  };

  return (
    <Box
      display="flex"
      alignItems="center"
      gap={1}
      px={2}
      py={1}
      borderBottom="1px solid"
      borderColor="base.divider"
      flexWrap="wrap"
      flexShrink={0}
    >
      {!hideHistory && (
        <>
          <IconButton
            icon={UndoIcon}
            aria-label="Undo"
            isDisabled={!state.canUndo}
            onClick={() => editor.chain().focus().undo().run()}
          />
          <IconButton
            icon={RedoIcon}
            aria-label="Redo"
            isDisabled={!state.canRedo}
            onClick={() => editor.chain().focus().redo().run()}
          />

          <Divider />
        </>
      )}

      <Menu>
        {/* MenuButton renders its own chevron — don't add another. */}
        <MenuButton variant="tertiary">
          {BLOCK_STYLE_LABELS[state.headingLevel]}
        </MenuButton>
        <MenuList>
          <MenuItem onClick={() => editor.chain().focus().setParagraph().run()}>
            Paragraph
          </MenuItem>
          {MENU_HEADING_LEVELS.map((level) => (
            <MenuItem
              key={level}
              onClick={() => editor.chain().focus().setHeading({ level }).run()}
            >
              {BLOCK_STYLE_LABELS[level]}
            </MenuItem>
          ))}
        </MenuList>
      </Menu>

      <Divider />

      <IconButton
        icon={BoldIcon}
        aria-label="Bold"
        variant={state.isBold && !isInHeading ? "secondary" : "tertiary"}
        // Headings are already semibold, the heaviest Lausanne cut, so Bold has no weight to add there.
        isDisabled={isInHeading}
        onClick={() => editor.chain().focus().toggleBold().run()}
      />
      <IconButton
        icon={ItalicIcon}
        aria-label="Italic"
        variant={state.isItalic ? "secondary" : "tertiary"}
        onClick={() => editor.chain().focus().toggleItalic().run()}
      />
      <IconButton
        icon={StrikethroughSIcon}
        aria-label="Strikethrough"
        variant={state.isStrike ? "secondary" : "tertiary"}
        onClick={() => editor.chain().focus().toggleStrike().run()}
      />
      <IconButton
        icon={CodeIcon}
        aria-label="Inline code"
        variant={state.isCode ? "secondary" : "tertiary"}
        onClick={() => editor.chain().focus().toggleCode().run()}
      />
      <IconButton
        icon={LinkIcon}
        aria-label="Link"
        variant={state.isLink ? "secondary" : "tertiary"}
        onClick={setLink}
      />

      <Divider />

      <IconButton
        icon={ListNumberedIcon}
        aria-label="Numbered list"
        variant={state.isOrderedList ? "secondary" : "tertiary"}
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
      />
      <IconButton
        icon={ListBulletedIcon}
        aria-label="Bulleted list"
        variant={state.isBulletList ? "secondary" : "tertiary"}
        onClick={() => editor.chain().focus().toggleBulletList().run()}
      />

      <Divider />

      <IconButton
        icon={CodeBlockIcon}
        aria-label="Code block"
        variant={state.isCodeBlock ? "secondary" : "tertiary"}
        onClick={() => editor.chain().focus().toggleCodeBlock().run()}
      />
      <Menu
        isOpen={isTableMenuOpen}
        onOpen={() => setIsTableMenuOpen(true)}
        onClose={() => setIsTableMenuOpen(false)}
      >
        <MenuIconButton
          icon={TableIcon}
          aria-label="Table"
          variant={state.isInTable ? "secondary" : "tertiary"}
        />
        <MenuList>
          {state.isInTable ? (
            <>
              <MenuItem
                onClick={() => editor.chain().focus().addRowAfter().run()}
              >
                Add row below
              </MenuItem>
              <MenuItem
                onClick={() => editor.chain().focus().addColumnAfter().run()}
              >
                Add column right
              </MenuItem>
              <MenuItem
                onClick={() => editor.chain().focus().deleteRow().run()}
              >
                Delete row
              </MenuItem>
              <MenuItem
                onClick={() => editor.chain().focus().deleteColumn().run()}
              >
                Delete column
              </MenuItem>
              <MenuItem
                onClick={() => editor.chain().focus().deleteTable().run()}
              >
                Delete table
              </MenuItem>
            </>
          ) : (
            <TableSizePicker
              onSelect={(rows, cols) => {
                editor
                  .chain()
                  .focus()
                  .insertTable({ rows, cols, withHeaderRow: true })
                  .run();
                setIsTableMenuOpen(false);
              }}
            />
          )}
        </MenuList>
      </Menu>

      {toolbarActions && (
        <>
          <Divider />
          {toolbarActions(editor)}
        </>
      )}
    </Box>
  );
};

type TableSizePickerProps = {
  /** Called with the chosen table dimensions when a grid cell is clicked. */
  onSelect: (rows: number, cols: number) => void;
};

/**
 * Google-Docs/Notion-style insert-table control: a grid of cells where
 * hovering highlights the top-left cols×rows block (and shows a live
 * "cols × rows" label) and clicking inserts a table of that size. The grid
 * starts at 10×5 with 1×1 selected and grows a column/row as the pointer
 * reaches the current edge (capped at 20×20), like Google Docs. Rendered as a
 * non-MenuItem child of the MenuList so the menu's keyboard/focus handling
 * leaves the interactive grid alone; the parent closes the menu on select.
 */
const TableSizePicker = ({ onSelect }: TableSizePickerProps) => {
  const [hovered, setHovered] = useState({ rows: 1, cols: 1 });

  const displayedCols = Math.min(
    TABLE_PICKER_MAX,
    Math.max(TABLE_PICKER_BASE_COLS, hovered.cols + 1),
  );
  const displayedRows = Math.min(
    TABLE_PICKER_MAX,
    Math.max(TABLE_PICKER_BASE_ROWS, hovered.rows + 1),
  );

  return (
    <Box px={3} py={2} onMouseLeave={() => setHovered({ rows: 1, cols: 1 })}>
      <Box display="inline-block">
        <Box
          display="grid"
          gridTemplateColumns={`repeat(${displayedCols}, 1fr)`}
          gap={`${TABLE_PICKER_GAP_PX}px`}
          width={`${
            displayedCols * TABLE_PICKER_CELL_PX +
            (displayedCols - 1) * TABLE_PICKER_GAP_PX
          }px`}
        >
          {Array.from({ length: displayedRows * displayedCols }, (_, index) => {
            const rows = Math.floor(index / displayedCols) + 1;
            const cols = (index % displayedCols) + 1;
            const isActive = rows <= hovered.rows && cols <= hovered.cols;
            return (
              <Box
                key={`${rows}x${cols}`}
                boxSize={`${TABLE_PICKER_CELL_PX}px`}
                border="1px solid"
                borderColor={isActive ? "primary.base" : "base.border"}
                borderRadius="sm"
                backgroundColor={isActive ? "primary.background" : "white"}
                cursor="pointer"
                onMouseEnter={() => setHovered({ rows, cols })}
                onClick={() => onSelect(rows, cols)}
              />
            );
          })}
        </Box>
        <Box mt={2} textAlign="center">
          <Text size="sm" color="text.secondary">
            {`${hovered.cols} × ${hovered.rows}`}
          </Text>
        </Box>
      </Box>
    </Box>
  );
};
